/**
 * Entitlements: what an organization may do, independent of WHY.
 *
 * Application code asks `entitlements.recurringAppointments` or
 * `entitlements.maxProviders`; it never asks "is this Pro?". Plans are
 * rows in the `plans` table whose `entitlements` JSON is validated
 * against this shape, so limits and features change by editing data.
 */

export interface Entitlements {
  /** Bookable providers included (before add-ons). */
  maxProviders: number;
  maxLocations: number;
  /**
   * SMS messages included per billing period. 0 means NO ALLOWANCE HAS
   * BEEN CONFIGURED — not "this plan has no SMS". `smsReminders` is what
   * says whether the plan includes appointment reminder SMS at all.
   * A platform administrator sets the number on the plan row.
   */
  smsIncluded: number;
  /** Appointment reminder SMS is part of this plan (begins with Shop). */
  smsReminders: boolean;
  removeBranding: boolean;
  calendarSync: boolean;
  recurringAppointments: boolean;
  deposits: boolean;
  advancedAvailability: boolean;
  advancedReporting: boolean;
  resources: boolean;
  apiAccess: boolean;
  customDomain: boolean;
  workforceAssembleIntegration: boolean;
  customReminderTiming: boolean;
  customMessages: boolean;
  reviewRequests: boolean;
  customerBlocking: boolean;
  waitlist: boolean;
  prioritySupport: boolean;
  /**
   * Promotional SMS and MMS. Not part of any plan: it is the separately
   * billed marketing module on the workspace's own subscription (0034),
   * so it is proved by the same paid subscription as everything else and
   * lapses with it.
   */
  marketingMessaging: boolean;
  /**
   * Products, custom orders and rentals. Like marketingMessaging it is
   * not part of any plan: it is a separately billed activity on the
   * business's own subscription (0040), so it is proved by the same paid
   * subscription as everything else and lapses with it.
   */
  ordering: boolean;
  /** Price of each provider beyond maxProviders, in cents per month; null = not offered. */
  extraProviderPriceCents: number | null;
}

export type EntitlementKey = keyof Entitlements;

export type BooleanEntitlement = {
  [K in EntitlementKey]: Entitlements[K] extends boolean ? K : never;
}[EntitlementKey];

/** The conservative baseline every organization has even if plan data is missing. */
export const BASE_ENTITLEMENTS: Entitlements = {
  maxProviders: 1,
  maxLocations: 1,
  smsIncluded: 0,
  smsReminders: false,
  removeBranding: false,
  calendarSync: false,
  recurringAppointments: false,
  deposits: false,
  advancedAvailability: false,
  advancedReporting: false,
  resources: false,
  apiAccess: false,
  customDomain: false,
  workforceAssembleIntegration: false,
  customReminderTiming: false,
  customMessages: false,
  reviewRequests: false,
  customerBlocking: false,
  waitlist: false,
  prioritySupport: false,
  marketingMessaging: false,
  ordering: false,
  extraProviderPriceCents: null,
};

/** Human copy for upgrade prompts; the admin UI shows these, never raw keys. */
export const ENTITLEMENT_LABELS: Record<BooleanEntitlement, { title: string; description: string }> = {
  removeBranding: { title: 'Remove "Powered by" branding', description: 'Show only your business on your booking page.' },
  smsReminders: { title: 'SMS appointment reminders', description: 'Remind customers by text message as well as email.' },
  calendarSync: { title: 'Calendar synchronization', description: 'Keep Google and Outlook calendars in step with bookings.' },
  recurringAppointments: { title: 'Recurring appointments', description: 'Book a standing weekly or monthly appointment in one step.' },
  deposits: { title: 'Deposits and full payment', description: 'Ask customers to pay a deposit or the full amount when they book.' },
  advancedAvailability: { title: 'Advanced availability rules', description: 'Different hours by date range, split shifts, and hours that differ by service.' },
  advancedReporting: { title: 'Advanced reporting', description: 'Bookings, no-shows and revenue by provider, service and period.' },
  resources: { title: 'Rooms, chairs and equipment', description: 'Schedule the things an appointment needs alongside the person.' },
  apiAccess: { title: 'API access', description: 'Connect your own systems to your bookings.' },
  customDomain: { title: 'Custom booking domain', description: 'Serve your booking page from your own domain.' },
  workforceAssembleIntegration: { title: 'Workforce Assemble integration', description: 'Connect employees, facilities and schedules across products.' },
  customReminderTiming: { title: 'Custom reminder timing', description: 'Choose when reminders go out.' },
  customMessages: { title: 'Custom confirmation text', description: 'Add directions, parking or preparation notes to confirmation and reminder emails.' },
  reviewRequests: { title: 'Review requests', description: 'Ask happy customers for a review after their appointment, at a link you choose.' },
  customerBlocking: { title: 'Customer blocking', description: 'Stop repeat no-shows from booking online.' },
  waitlist: { title: 'Waitlist', description: 'Let customers ask to be told when a full day opens up, and tell them the moment it does.' },
  prioritySupport: { title: 'Priority support', description: 'Faster answers when something matters.' },
  marketingMessaging: { title: 'Marketing messages', description: 'Send promotional texts and picture messages to customers who have agreed to hear from you.' },
  ordering: { title: 'Products, custom orders and rentals', description: 'Sell or rent anything you like from a page of your own, with your own categories, quantities, photos, deposits and order statuses.' },
};

export interface PlanPublic {
  code: string;
  name: string;
  tagline: string;
  monthly_price_cents: number;
  /** Per month, when billed annually. */
  annual_monthly_price_cents: number;
  annual_price_cents: number;
  currency: string;
  highlights: string[];
  entitlements: Entitlements;
  sort_order: number;
}

export function annualSavingsPercent(plan: Pick<PlanPublic, 'monthly_price_cents' | 'annual_price_cents'>): number {
  if (plan.monthly_price_cents === 0) return 0;
  const monthlyTotal = plan.monthly_price_cents * 12;
  return Math.round(((monthlyTotal - plan.annual_price_cents) / monthlyTotal) * 100);
}

export function formatPrice(cents: number, currency = 'USD', locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
}
